import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Upload, FileText, AlertCircle, Download } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

const COURSE_CATEGORIES = {
  "Instruments": [
    "Drums",
    "Bass",
    "Piano",
    "Guitar",
    "Trumpet",
    "Trombone",
    "Clarinet",
    "Flute",
    "Oboe",
    "Alto Sax",
    "Tenor Sax",
    "Soprano Sax",
    "Baritone Sax",
    "Vocal",
  ],
  "Theory & Composition": [
    "Music Theory",
    "Jazz Theory",
    "Arrangement",
    "Improvisation",
  ],
  "Production": [
    "Studio Production",
    "AI Creative Music",
  ],
};

// Flat list for validation
const ALL_COURSES = Object.values(COURSE_CATEGORIES).flat();

const SERVICE_AGREEMENT = `Muller Music School (MMS) Instructor Service Agreement

1. Independent Contractor Status
The Instructor acknowledges that they are performing services as an Independent Contractor and not as an employee of Muller Music School. The Instructor is responsible for their own self-employment taxes, insurance, and professional certifications.

2. Compensation & Payment Terms
• Rate: Compensation is set at a flat rate of $27.50 per hour for course instruction, or $22.00 per 30-minute lesson.
• Reporting: Instructors must log their teaching hours through the MMS Instructor Portal immediately following each session.
• Payment Cycle: Payments are processed every two weeks based on the hours logged in the portal.
• Reset Logic: Once the Admin processes a payment, the "Amount Due" in the portal will reset to zero, and the record will move to the Payment History.

3. Confidentiality & Course Materials
All materials provided in the Course Materials section are the intellectual property of Muller Music School. Instructors are granted a limited license to use these materials for teaching MMS students only. Distribution, reproduction, or sale of these materials to third parties is strictly prohibited.

4. Data Accuracy & Legal Information
The Instructor certifies that all legal information provided in their profile (Full Legal Name, Tax ID/SSN, Address) is accurate. This information will be used for payment processing and any necessary legal or tax reporting (e.g., Form 1099).

5. Liability Waiver
Muller Music School is not liable for any technical interruptions during online instruction or personal injury occurring during in-person instruction. The Instructor agrees to conduct themselves according to the school's professional code of conduct.

6. Age Requirement
The Instructor certifies that they are at least 18 years of age.

7. Contract Requirement
The Instructor must upload a signed copy of this agreement as a PDF file to complete registration and activate their account.`;

export const InstructorRegister = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const contractInputRef = useRef(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    tax_id_ssn: "",
    physical_address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    authorized_courses: [],
    digital_signature: "",
    agreed_to_terms: false,
    contract_file: null,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const toggleCourse = (course) => {
    setFormData((prev) => ({
      ...prev,
      authorized_courses: prev.authorized_courses.includes(course)
        ? prev.authorized_courses.filter((c) => c !== course)
        : [...prev.authorized_courses, course],
    }));
  };

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Name validation - must have at least 2 characters and only letters/spaces
  const isValidName = (name) => {
    const nameRegex = /^[a-zA-Z\s'-]{2,}$/;
    return nameRegex.test(name.trim());
  };

  // Age calculation
  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    // First name validation
    if (!formData.first_name) {
      newErrors.first_name = "First name is required";
    } else if (!isValidName(formData.first_name)) {
      newErrors.first_name = "Please enter a valid first name (letters only, min 2 characters)";
    }
    
    // Last name validation
    if (!formData.last_name) {
      newErrors.last_name = "Last name is required";
    } else if (!isValidName(formData.last_name)) {
      newErrors.last_name = "Please enter a valid last name (letters only, min 2 characters)";
    }
    
    // Date of birth / Age validation
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required";
    } else {
      const age = calculateAge(formData.date_of_birth);
      if (age < 18) {
        newErrors.date_of_birth = "You must be at least 18 years old to register";
      } else if (age > 100) {
        newErrors.date_of_birth = "Please enter a valid date of birth";
      }
    }
    
    if (!formData.tax_id_ssn) newErrors.tax_id_ssn = "Tax ID/SSN is required";
    if (!formData.physical_address) newErrors.physical_address = "Physical address is required";
    if (!formData.emergency_contact_name) newErrors.emergency_contact_name = "Emergency contact name is required";
    if (!formData.emergency_contact_phone) newErrors.emergency_contact_phone = "Emergency contact phone is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (formData.authorized_courses.length === 0) {
      newErrors.authorized_courses = "Select at least one course";
    }
    if (!formData.digital_signature) {
      newErrors.digital_signature = "Digital signature is required";
    }
    if (!formData.agreed_to_terms) {
      newErrors.agreed_to_terms = "You must agree to the service agreement";
    }
    if (!formData.contract_file) {
      newErrors.contract_file = "You must upload a signed contract PDF";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContractSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Only PDF files are allowed");
        return;
      }
      if (file.type !== 'application/pdf') {
        toast.error("Invalid file type. Only PDF files are allowed");
        return;
      }
      handleChange("contract_file", file);
      toast.success("Contract file selected");
    }
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    try {
      // First register the instructor
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        date_of_birth: formData.date_of_birth,
        tax_id_ssn: formData.tax_id_ssn,
        physical_address: formData.physical_address,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_phone: formData.emergency_contact_phone,
        authorized_courses: formData.authorized_courses,
        digital_signature: formData.digital_signature,
        agreed_to_terms: formData.agreed_to_terms,
      });

      const token = response.data.access_token;
      localStorage.setItem("mms_token", token);
      localStorage.setItem("mms_user_type", response.data.user_type);
      localStorage.setItem("mms_user_id", response.data.user_id);

      // Now upload the contract
      const contractFormData = new FormData();
      contractFormData.append("file", formData.contract_file);

      await axios.post(`${API}/instructor/upload-contract`, contractFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Registration successful! Your account is now active.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {theme === 'dark' ? (
          <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-l from-zinc-800 to-zinc-900 rounded-full blur-3xl opacity-20" />
        ) : (
          <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-l from-indigo-100 to-blue-100 rounded-full blur-3xl opacity-50" />
        )}
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <button
          onClick={() => navigate("/")}
          className={`flex items-center gap-2 transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <img src={logoUrl} alt="MMS Logo" className="h-12 w-auto object-contain logo-glass" />
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-2xl mx-auto px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 md:p-12"
        >
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-3 mb-10">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all ${
                  step >= s
                    ? theme === 'dark' 
                      ? "bg-white text-black border-white"
                      : "bg-indigo-600 text-white border-indigo-600"
                    : theme === 'dark'
                      ? "bg-transparent text-slate-500 border-white/20"
                      : "bg-transparent text-slate-400 border-slate-300"
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
            ))}
          </div>

          <h2 className={`text-2xl font-bold mb-2 text-center ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
            {step === 1 && "Create Account"}
            {step === 2 && "Legal Information"}
            {step === 3 && "Courses & Agreement"}
          </h2>
          <p className={`text-center mb-8 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            {step === 1 && "Set up your instructor account credentials"}
            {step === 2 && "Provide your legal profile for tax reporting"}
            {step === 3 && "Select courses, upload contract, and accept agreement"}
          </p>

          {/* Step 1: Account */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="form-label">Email Address</label>
                <input
                  data-testid="register-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="glass-input w-full"
                  placeholder="instructor@example.com"
                />
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>
              <div>
                <label className="form-label">Password</label>
                <input
                  data-testid="register-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  className="glass-input w-full"
                  placeholder="Minimum 6 characters"
                />
                {errors.password && <p className="form-error">{errors.password}</p>}
              </div>
              <div>
                <label className="form-label">Confirm Password</label>
                <input
                  data-testid="register-confirm-password-input"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  className="glass-input w-full"
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && <p className="form-error">{errors.confirmPassword}</p>}
              </div>
            </div>
          )}

          {/* Step 2: Legal Info */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">First Name</label>
                  <input
                    data-testid="register-firstname-input"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    className="glass-input w-full"
                    placeholder="John"
                  />
                  {errors.first_name && <p className="form-error">{errors.first_name}</p>}
                </div>
                <div>
                  <label className="form-label">Last Name</label>
                  <input
                    data-testid="register-lastname-input"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    className="glass-input w-full"
                    placeholder="Doe"
                  />
                  {errors.last_name && <p className="form-error">{errors.last_name}</p>}
                </div>
              </div>
              <div>
                <label className="form-label">Date of Birth (Must be 18+)</label>
                <input
                  data-testid="register-dob-input"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleChange("date_of_birth", e.target.value)}
                  className="glass-input w-full"
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                />
                {errors.date_of_birth && (
                  <p className="form-error flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.date_of_birth}
                  </p>
                )}
              </div>
              <div>
                <label className="form-label">Tax ID / SSN</label>
                <input
                  data-testid="register-taxid-input"
                  type="text"
                  value={formData.tax_id_ssn}
                  onChange={(e) => handleChange("tax_id_ssn", e.target.value)}
                  className="glass-input w-full mono"
                  placeholder="XXX-XX-XXXX"
                />
                {errors.tax_id_ssn && <p className="form-error">{errors.tax_id_ssn}</p>}
              </div>
              <div>
                <label className="form-label">Physical Address</label>
                <textarea
                  data-testid="register-address-input"
                  value={formData.physical_address}
                  onChange={(e) => handleChange("physical_address", e.target.value)}
                  className="glass-input w-full min-h-[80px] resize-none"
                  placeholder="Street, City, State, ZIP"
                />
                {errors.physical_address && <p className="form-error">{errors.physical_address}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Emergency Contact Name</label>
                  <input
                    data-testid="register-emergency-name-input"
                    type="text"
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleChange("emergency_contact_name", e.target.value)}
                    className="glass-input w-full"
                    placeholder="Contact name"
                  />
                  {errors.emergency_contact_name && (
                    <p className="form-error">{errors.emergency_contact_name}</p>
                  )}
                </div>
                <div>
                  <label className="form-label">Emergency Contact Phone</label>
                  <input
                    data-testid="register-emergency-phone-input"
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => handleChange("emergency_contact_phone", e.target.value)}
                    className="glass-input w-full"
                    placeholder="(555) 123-4567"
                  />
                  {errors.emergency_contact_phone && (
                    <p className="form-error">{errors.emergency_contact_phone}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Courses & Agreement */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="form-label">Select Courses You Can Teach</label>
                <div className="space-y-6 mt-3">
                  {Object.entries(COURSE_CATEGORIES).map(([category, courses]) => (
                    <div key={category}>
                      <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                        <span className={`w-2 h-2 rounded-full ${
                          category === 'Instruments' ? 'bg-blue-500' :
                          category === 'Theory & Composition' ? 'bg-purple-500' : 'bg-emerald-500'
                        }`} />
                        {category}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {courses.map((course) => (
                          <button
                            key={course}
                            data-testid={`course-${course.toLowerCase().replace(/\s+/g, "-")}`}
                            type="button"
                            onClick={() => toggleCourse(course)}
                            className={`p-3 rounded-xl border text-left text-sm transition-all ${
                              formData.authorized_courses.includes(course)
                                ? theme === 'dark'
                                  ? "bg-white text-black border-white"
                                  : "bg-indigo-600 text-white border-indigo-600"
                                : theme === 'dark'
                                  ? "bg-white/5 text-white border-white/10 hover:border-white/20"
                                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            {course}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {errors.authorized_courses && (
                  <p className="form-error">{errors.authorized_courses}</p>
                )}
              </div>

              {/* Service Agreement */}
              <div className={`glass-card p-4 ${theme === 'dark' ? 'bg-zinc-900/50' : 'bg-slate-100'}`}>
                <button
                  type="button"
                  onClick={() => setShowAgreement(!showAgreement)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>MMS Service Agreement</span>
                  {showAgreement ? (
                    <ChevronUp className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                  ) : (
                    <ChevronDown className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                  )}
                </button>
                {showAgreement && (
                  <div
                    data-testid="service-agreement-text"
                    className={`mt-4 p-4 rounded-xl max-h-64 overflow-y-auto text-sm whitespace-pre-wrap ${
                      theme === 'dark' ? 'bg-black/30 text-slate-300' : 'bg-white text-slate-600'
                    }`}
                  >
                    {SERVICE_AGREEMENT}
                  </div>
                )}
              </div>

              {/* Contract Upload */}
              <div>
                <label className="form-label">Upload Signed Contract (PDF only)</label>
                
                {/* Download Template Button */}
                <a
                  href={`${API}/contract-template`}
                  download="MMS_Service_Agreement.pdf"
                  data-testid="download-contract-template"
                  className={`flex items-center justify-center gap-2 w-full p-3 mb-3 rounded-xl border transition-all ${
                    theme === 'dark'
                      ? "border-blue-500/50 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                      : "border-blue-500 bg-blue-50 text-blue-600 hover:bg-blue-100"
                  }`}
                >
                  <Download className="w-5 h-5" />
                  <span>Download Contract Template</span>
                </a>
                
                <input
                  ref={contractInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleContractSelect}
                  className="hidden"
                  data-testid="contract-file-input"
                />
                <button
                  type="button"
                  onClick={() => contractInputRef.current?.click()}
                  className={`w-full p-4 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-3 ${
                    formData.contract_file
                      ? theme === 'dark'
                        ? "border-green-500/50 bg-green-500/10"
                        : "border-green-500 bg-green-50"
                      : theme === 'dark'
                        ? "border-white/20 hover:border-white/40 bg-white/5"
                        : "border-slate-300 hover:border-slate-400 bg-slate-50"
                  }`}
                >
                  {formData.contract_file ? (
                    <>
                      <FileText className={`w-6 h-6 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                      <span className={theme === 'dark' ? 'text-green-400' : 'text-green-600'}>
                        {formData.contract_file.name}
                      </span>
                      <Check className={`w-5 h-5 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                    </>
                  ) : (
                    <>
                      <Upload className={`w-6 h-6 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                      <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                        Click to upload signed contract PDF
                      </span>
                    </>
                  )}
                </button>
                {errors.contract_file && <p className="form-error">{errors.contract_file}</p>}
                <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                  Download the template above, sign it, scan/photograph, then upload
                </p>
              </div>

              <div>
                <label className="form-label">Digital Signature (Type your full legal name)</label>
                <input
                  data-testid="register-signature-input"
                  type="text"
                  value={formData.digital_signature}
                  onChange={(e) => handleChange("digital_signature", e.target.value)}
                  className="glass-input w-full"
                  style={{ fontFamily: "'Dancing Script', cursive, sans-serif" }}
                  placeholder="Your full legal name"
                />
                {errors.digital_signature && (
                  <p className="form-error">{errors.digital_signature}</p>
                )}
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  data-testid="agree-terms-checkbox"
                  checked={formData.agreed_to_terms}
                  onCheckedChange={(checked) => handleChange("agreed_to_terms", checked)}
                  className="mt-0.5"
                />
                <label className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowAgreement(true)}
                    className={`underline ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`}
                  >
                    MMS Service Agreement
                  </button>
                </label>
              </div>
              {errors.agreed_to_terms && (
                <p className="form-error">{errors.agreed_to_terms}</p>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-4 mt-10">
            {step > 1 && (
              <button
                data-testid="register-back-btn"
                type="button"
                onClick={() => setStep(step - 1)}
                className="glass-button-secondary flex-1"
              >
                Back
              </button>
            )}
            {step < 3 ? (
              <button
                data-testid="register-next-btn"
                type="button"
                onClick={handleNext}
                className="glass-button-primary flex-1"
              >
                Continue
              </button>
            ) : (
              <button
                data-testid="register-submit-btn"
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="glass-button-primary flex-1 disabled:opacity-50"
              >
                {loading ? "Creating Account..." : "Complete Registration"}
              </button>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
};
